import {RefObject, useCallback, useEffect, useState, useRef} from 'react';
import {safeCaptureException} from '../../../utils/sentry';
import {WebcamRefTypes} from '@chekinapp/ui';
import {throttle, useSyncRef} from '@chekin/core';
import type {
  FacePosition,
  OnFaceDetectionSignal,
  ServerDetectionResponse,
} from '../types';
import {useFaceDetectionSocket} from './useFaceDetectionSocket';

const FACE_POSITION_THROTTLE = 150;
const FRAME_INTERVAL = 150;

interface UseServerFaceDetectionProps {
  videoRef?: RefObject<WebcamRefTypes>;
  shouldInitialize?: boolean;
  onSignal?: OnFaceDetectionSignal;
}

export const useServerFaceDetection = ({
  videoRef,
  shouldInitialize = true,
  onSignal,
}: UseServerFaceDetectionProps) => {
  const [facePosition, setFacePosition] = useState<FacePosition>('unknown');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onSignalRef = useSyncRef(onSignal);

  const throttledSetFacePosition = useRef(
    throttle((position: FacePosition) => {
      setFacePosition(position);
    }, FACE_POSITION_THROTTLE),
  ).current;

  const mapServerDirection = useCallback((direction: string): FacePosition => {
    switch (direction.toUpperCase()) {
      case 'LEFT':
        return 'left';
      case 'RIGHT':
        return 'right';
      case 'UP':
        return 'up';
      case 'DOWN':
        return 'down';
      case 'CENTER':
        return 'center';
      default:
        return 'unknown';
    }
  }, []);

  const handleMessage = useCallback(
    (response: ServerDetectionResponse) => {
      if (response.type !== 'detection') return;

      const {is_face_detected, direction, is_blinking, move_percent} = response.data;

      const position: FacePosition =
        is_face_detected && (direction === 'CENTER' || move_percent === 100)
          ? mapServerDirection(direction)
          : 'unknown';

      throttledSetFacePosition(position);

      onSignalRef.current?.({
        source: 'server',
        facePosition: position,
        direction,
        movePercent: move_percent,
        isBlinking: is_blinking,
      });
    },
    [mapServerDirection, throttledSetFacePosition, onSignalRef],
  );

  const {isConnected, sendData} = useFaceDetectionSocket({
    onMessage: handleMessage,
    autoConnect: shouldInitialize,
  });
  const isConnectedRef = useSyncRef(isConnected);
  const shouldInitializeRef = useSyncRef(shouldInitialize);

  useEffect(() => {
    if (!shouldInitialize) {
      return;
    }

    canvasRef.current = document.createElement('canvas');
    return () => {
      canvasRef.current = null;
    };
  }, [shouldInitialize]);

  const sendFrame = useCallback(() => {
    if (
      !shouldInitializeRef.current ||
      !isConnectedRef.current ||
      !canvasRef.current ||
      !videoRef?.current?.video
    ) {
      return;
    }

    const video = videoRef.current.video;
    if (video.readyState !== 4) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      async blob => {
        if (!blob) return;

        try {
          const arrayBuffer = await blob.arrayBuffer();
          sendData(arrayBuffer);
        } catch (err) {
          safeCaptureException(err, 'Preparing a face frame for detection');
          console.error('Error preparing frame for WebSocket:', err);
        }
      },
      'image/jpeg',
      0.8,
    );
  }, [isConnectedRef, sendData, videoRef, shouldInitializeRef]);

  const startDetection = useCallback(() => {
    if (!shouldInitialize) {
      return;
    }

    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
    }

    frameIntervalRef.current = setInterval(sendFrame, FRAME_INTERVAL);
  }, [sendFrame, shouldInitialize]);

  useEffect(() => {
    return () => {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }
    };
  }, []);

  return {
    isModelLoading: shouldInitialize ? !isConnected : false,
    facePosition,
    startDetection,
  };
};
